import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@monksflow/monks-ui';
import { SparklesIcon } from 'lucide-react';
import {
  PANEL_RESIZE_TRACK_WIDTH,
  PanelResizeHandle,
  PanelReveal,
  PanelToggle,
  usePanelWidth,
} from '@/shared/ui';
import type { PageAgentChat } from '../../model/page-agent';
import type { PageAgentPanelState } from '../../model/usePageAgentPanel';
import { usePageAgentRenderer } from '../../model/page-agent-renderer';

/** Wide enough for a question card's options to read on one line each. */
const DEFAULT_PANEL_WIDTH = 400;

interface PageAgentPanelProps {
  panel: PageAgentPanelState;
  /** The agent's name, which heads the panel. */
  label: string;
  children: ReactNode;
}

/**
 * A page's agent, docked on the left. Its contents stay mounted while it is closed, so the
 * conversation survives a collapse.
 */
export function PageAgentPanel({ panel, label, children }: PageAgentPanelProps) {
  const { t } = useTranslation('chats');
  const { id, isOpen, setIsOpen } = panel;
  const { width, setWidth, commitWidth } = usePanelWidth(
    `page-agent.${id}.width`,
    DEFAULT_PANEL_WIDTH,
  );

  return (
    <PanelReveal
      isOpen={isOpen}
      axis="width"
      contentAnchor="end"
      gapEdge="right"
      keepMounted
      className="h-full min-h-0"
    >
      {/* The list-page surface the panel sits beside, not Content Studio's PanelShell, so
          its corners match the page's. */}
      <section
        aria-label={label}
        data-page-agent-panel
        style={{ width: width + PANEL_RESIZE_TRACK_WIDTH }}
        className="flex h-full min-h-0 flex-row rounded-lg bg-card"
      >
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex items-center justify-between gap-xs border-b border-border-light py-xs pr-xs pl-md">
            <span className="type-body-md-strong">{label}</span>
            <PanelToggle
              side="left"
              isOpen
              onOpenChange={() => setIsOpen(false)}
              label={t('pageAgent.close')}
            />
          </div>
          <div className="flex min-h-0 flex-1 flex-col">{children}</div>
        </div>
        {/* A gutter inside the panel rather than over its edge, as Content Studio's left panel. */}
        <PanelResizeHandle
          side="left"
          width={width}
          onResize={setWidth}
          onResizeEnd={commitWidth}
          label={t('pageAgent.resize')}
        />
      </section>
    </PanelReveal>
  );
}

interface PageAgentDockProps {
  panel: PageAgentPanelState;
  /** Nothing is docked until the page has its agent's chat. */
  chat: PageAgentChat | null | undefined;
}

/** The panel with the agent's chat in it, as the app draws one. */
export function PageAgentDock({ panel, chat }: PageAgentDockProps) {
  const render = usePageAgentRenderer();
  if (!render || !chat) return null;
  return (
    <PageAgentPanel panel={panel} label={chat.relay.agentName}>
      {render(chat)}
    </PageAgentPanel>
  );
}

/**
 * Reopens a closed panel, and is not there while it is open or while there is no agent to open.
 * Drawn as Flow.Assist's open button is, inside the page's own content.
 */
export function OpenPageAgentButton({ panel, chat }: PageAgentDockProps) {
  const render = usePageAgentRenderer();
  if (panel.isOpen || !render || !chat) return null;
  return (
    <Button
      size="sm"
      variant="outline"
      className="shrink-0 gap-xs bg-background"
      onClick={() => panel.setIsOpen(true)}
    >
      <SparklesIcon className="size-4 text-primary" aria-hidden />
      {chat.relay.agentName}
    </Button>
  );
}
